import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  IconButton,
  InputAdornment,
  TextField,
  Chip,
  Divider,
  Badge,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  TablePagination,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Message as MessageIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  Archive as ArchiveIcon,
  Refresh as RefreshIcon,
  MarkChatRead as MarkChatReadIcon,
  InsertPhoto as ImageIcon,
  AttachFile as AttachFileIcon,
  Campaign as CampaignIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import pb from './pocketbase';

const SupportMessages = () => {
  const theme = useTheme();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // State management
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalConversations, setTotalConversations] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // New state for selected conversation and message input
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
   // Fetch messages for selected conversation
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedConversation) return;
      
      setLoadingMessages(true);
      try {
        const result = await pb.collection('admin_chat').getList(1, 100, {
          filter: `user = "${selectedConversation.userId}"`,
          sort: 'created',
          expand: 'user',
        });
        
        setSelectedMessages(result.items);
        
        // Mark unread messages as read
        const unreadMessages = result.items.filter(msg => 
          msg.sender === 'user' && !msg.seen
        );
        
        if (unreadMessages.length > 0) {
          await Promise.all(unreadMessages.map(msg => 
            pb.collection('admin_chat').update(msg.id, { seen: true })
          ));
        }
      } catch (err) {
        console.error('Error fetching messages:', err);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();

    // Set up real-time subscription for new messages
    if (selectedConversation) {
      const subscription = pb.collection('admin_chat').subscribe('*', function(e) {
        if (e.record && e.record.user === selectedConversation.userId) {
          if (e.action === 'create') {
            setSelectedMessages(prev => [...prev, e.record]);
            if (e.record.sender === 'user' && !e.record.seen) {
              pb.collection('admin_chat').update(e.record.id, { seen: true });
            }
          } else if (e.action === 'update') {
            setSelectedMessages(prev => 
              prev.map(msg => msg.id === e.record.id ? e.record : msg)
            );
          } else if (e.action === 'delete') {
            setSelectedMessages(prev => 
              prev.filter(msg => msg.id !== e.record.id)
            );
          }
        }
      });

      return () => {
        pb.collection('admin_chat').unsubscribe('*');
      };
    }
  }, [selectedConversation]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedMessages]);

  // Format date for messages
  const formatMessageDate = (dateString) => {
    const messageDate = new Date(dateString);
    const now = new Date();
    
    if (messageDate.toDateString() === now.toDateString()) {
      return format(messageDate, 'h:mm a');
    }
    
    if (now - messageDate < 7 * 24 * 60 * 60 * 1000) {
      return format(messageDate, 'EEEE h:mm a');
    }
    
    return format(messageDate, 'MMM d, yyyy h:mm a');
  };

  // Handle sending a new message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if ((!newMessage || newMessage.trim() === '') && selectedImages.length === 0) {
      return;
    }

    try {
      setSending(true);
      
      const messageData = {
        message: newMessage.trim(),
        user: selectedConversation.userId,
        sender: 'support',
        seen: false
      };

      if (selectedImages.length > 0) {
        const formData = new FormData();
        selectedImages.forEach(file => {
          formData.append('image', file);
        });
        Object.keys(messageData).forEach(key => {
          formData.append(key, messageData[key]);
        });
        await pb.collection('admin_chat').create(formData);
      } else {
        await pb.collection('admin_chat').create(messageData);
      }

      setNewMessage('');
      setSelectedImages([]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedImages(prevImages => [...prevImages, ...files]);
    event.target.value = '';
  };

  // Remove a selected image
  const handleRemoveImage = (index) => {
    setSelectedImages(prevImages => 
      prevImages.filter((_, i) => i !== index)
    );
  };

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        
        // Create filter based on selected filter option and search term
        let filter = '';
        
        if (selectedFilter === 'unread') {
          // Fix: filter for messages where sender is 'user' and seen is false
          filter = 'sender = "user" && seen = false';
        } 
        
        if (searchTerm) {
          // Fix: improve search to include message content and user email/name
          const searchFilter = `message ~ "${searchTerm}" || expand.user.email ~ "${searchTerm}" || expand.user.name ~ "${searchTerm}"`;
          filter = filter ? `${filter} && (${searchFilter})` : searchFilter;
        }
        
        try {
          // Get messages from admin_chat collection
          const result = await pb.collection('admin_chat').getList(1, 500, {
            filter,
            sort: '-created',
            expand: 'user',
          });
          
          // Group messages by user to create conversations
          const messagesGroupedByUser = {};
          
          // Make sure we log what we're working with for debugging
          console.log('Messages from API:', result.items);
          
          result.items.forEach(message => {
            if (!messagesGroupedByUser[message.user]) {
              messagesGroupedByUser[message.user] = {
                userId: message.user,
                messages: [],
                lastMessage: null,
                created: null,
                unreadCount: 0,
                userName: message.expand?.user?.name || null,
                userEmail: message.expand?.user?.email || null,
                userAvatar: message.expand?.user?.avatar || null
              };
            }
            
            // Add message to group
            messagesGroupedByUser[message.user].messages.push(message);
            
            // Keep track of last message - more reliable timestamp comparison
            const messageDate = new Date(message.created).getTime();
            const currentLastMessageDate = messagesGroupedByUser[message.user].created ? 
              new Date(messagesGroupedByUser[message.user].created).getTime() : 0;
              
            if (!messagesGroupedByUser[message.user].lastMessage || messageDate > currentLastMessageDate) {
              messagesGroupedByUser[message.user].lastMessage = message.message || (message.image && message.image.length > 0 ? '(Image attachment)' : '');
              messagesGroupedByUser[message.user].created = message.created;
              messagesGroupedByUser[message.user].hasImage = message.image && message.image.length > 0;
              messagesGroupedByUser[message.user].seen = message.seen;
            }
            
            // Count unread messages
            if (message.sender === 'user' && !message.seen) {
              messagesGroupedByUser[message.user].unreadCount++;
            }
          });
          
          console.log('Grouped messages:', messagesGroupedByUser);
          
          // Convert to array and sort by most recent message
          const conversationsArray = Object.values(messagesGroupedByUser)
            .sort((a, b) => new Date(b.created) - new Date(a.created));
          
          // Only apply pagination after sorting
          const paginatedConversations = conversationsArray.slice(
            page * rowsPerPage, 
            page * rowsPerPage + rowsPerPage
          );
          
          console.log('Conversation array:', conversationsArray);
          console.log('Setting conversations:', paginatedConversations);
          
          setConversations(paginatedConversations);
          setTotalConversations(conversationsArray.length);
          
          // Count total unread conversations
          const totalUnread = conversationsArray.reduce(
            (sum, conv) => sum + (conv.unreadCount > 0 ? 1 : 0), 
            0
          );
            
          setUnreadCount(totalUnread);
          
          setError(null);
        } catch (err) {
          console.error('Error fetching conversations:', err);
          
          // Check if this is a collection not found error
          if (err.status === 404 || err.message?.includes('not found')) {
            setError('Support chat system is not set up. Please create the admin_chat collection in PocketBase.');
            setConversations([]);
            setTotalConversations(0);
          } else {
            setError('Failed to load conversations. Please try again.');
          }
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchConversations();
  }, [searchTerm, selectedFilter, page, rowsPerPage, refreshKey]);

  // Handle filter change
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (filter) => {
    setSelectedFilter(filter);
    setPage(0);
    handleFilterClose();
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Refresh conversations
  const handleRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  return (
    <Box sx={{ 
      p: 3, 
      height: 'calc(100vh - 80px)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Support Messages
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage and respond to customer support inquiries
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        gap: 2,
        flexGrow: 1,
        minHeight: 0 // Important for proper scrolling
      }}>
        {/* Conversations List */}
        <Box sx={{ 
          width: { xs: '100%', md: 320 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: selectedConversation ? 0 : '100%', md: '100%' },
          overflow: 'hidden',
          transition: 'height 0.3s ease-in-out'
        }}>
          <Paper sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Search and Filters */}
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Search messages..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={handleFilterClick}
                  size="medium"
                  fullWidth
                >
                  {selectedFilter === 'all' ? 'All Messages' : 'Unread Messages'}
                </Button>
                {selectedFilter !== 'all' && (
                  <Button 
                    variant="text" 
                    color="error" 
                    onClick={() => handleFilterSelect('all')}
                    size="medium"
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Box>
            <Divider />
            
            {/* Conversations List */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="error">{error}</Alert>
                </Box>
              ) : conversations.length === 0 ? (
                <Box sx={{ 
                  py: 10, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <MessageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No conversations found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400, mb: 2 }}>
                    {searchTerm || selectedFilter !== 'all' 
                      ? 'Try adjusting your search or filters' 
                      : 'When customers contact support, their messages will appear here'}
                  </Typography>
                </Box>
              ) : (
                conversations.map(conversation => (
                  <Card 
                    key={conversation.userId}
                    elevation={0}
                    onClick={() => setSelectedConversation(conversation)}
                    sx={{ 
                      mb: 1, 
                      mx: 1,
                      cursor: 'pointer',
                      borderRadius: 2,
                      bgcolor: selectedConversation?.userId === conversation.userId
                        ? theme.palette.primary.light
                        : conversation.unreadCount > 0 
                        ? theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(66, 165, 245, 0.08)'
                        : 'background.paper',
                      '&:hover': {
                        bgcolor: selectedConversation?.userId === conversation.userId
                          ? theme.palette.primary.light
                          : theme.palette.action.hover
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            variant="dot"
                            color={conversation.unreadCount > 0 ? "primary" : "default"}
                          >
                            <Avatar 
                              alt={conversation.userName || 'User'}
                              src={conversation.userAvatar ? `${pb.baseUrl}/api/files/_pb_users_auth_/${conversation.userId}/${conversation.userAvatar}` : ''}
                              sx={{ bgcolor: theme.palette.primary.main }}
                            >
                              {(conversation.userName || 'U')[0].toUpperCase()}
                            </Avatar>
                          </Badge>
                        </Grid>
                        <Grid item xs>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {conversation.userName || `User #${conversation.userId.slice(0, 8)}`}
                            </Typography>
                            {conversation.unreadCount > 0 && (
                              <Chip 
                                label={conversation.unreadCount} 
                                color="primary" 
                                size="small"
                                sx={{ height: 20, fontSize: '0.75rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {formatDistanceToNow(new Date(conversation.created), { addSuffix: true })}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mt: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%'
                            }}
                          >
                            {conversation.lastMessage || 'No messages'}
                            {conversation.hasImage && (
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                                <ImageIcon fontSize="small" sx={{ fontSize: 14 }} />
                              </Box>
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
            
            {/* Pagination */}
            {conversations.length > 0 && (
              <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
                <TablePagination
                  component="div"
                  count={totalConversations}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </Box>
            )}
          </Paper>
        </Box>

        {/* Messages View */}
        <Box sx={{ 
          flexGrow: 1,
          height: { xs: selectedConversation ? '100%' : 0, md: '100%' },
          overflow: 'hidden',
          transition: 'height 0.3s ease-in-out'
        }}>
          <Paper sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            position: 'relative'
          }}>
            {selectedConversation && (
              <Box sx={{ 
                position: 'absolute',
                top: theme.spacing(1),
                left: theme.spacing(1),
                display: { xs: 'block', md: 'none' },
                zIndex: 3,
              }}>
                <IconButton
                  onClick={() => setSelectedConversation(null)}
                  sx={{ 
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': { bgcolor: 'background.paper' }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Box>
            )}
            {selectedConversation ? (
              <>
                {/* Messages Container */}
                <Box sx={{ 
                  flexGrow: 1, 
                  overflow: 'auto',
                  p: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)'
                }}>
                  {loadingMessages ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress />
                    </Box>
                  ) : selectedMessages.length === 0 ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      justifyContent: 'center', 
                      height: '100%' 
                    }}>
                      <CampaignIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No messages yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
                        Start the conversation by sending a message
                      </Typography>
                    </Box>
                  ) : (
                    selectedMessages.map((message, index) => {
                      const isFromUser = message.sender === 'user';
                      const hasImages = message.image && message.image.length > 0;
                      
                      return (
                        <Box
                          key={message.id}
                          sx={{
                            display: 'flex',
                            justifyContent: isFromUser ? 'flex-start' : 'flex-end',
                            mb: 2,
                          }}
                        >
                          {isFromUser && (
                            <Avatar 
                              sx={{ 
                                width: 32, 
                                height: 32, 
                                mr: 1,
                                mt: 'auto',
                                bgcolor: theme.palette.primary.main
                              }}
                            >
                              {(selectedConversation.userName || 'U')[0].toUpperCase()}
                            </Avatar>
                          )}
                          
                          <Box sx={{ maxWidth: '70%' }}>
                            <Card
                              sx={{
                                bgcolor: isFromUser 
                                  ? theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'
                                  : theme.palette.primary.main,
                                color: isFromUser 
                                  ? 'text.primary' 
                                  : theme.palette.primary.contrastText,
                                borderRadius: isFromUser ? '0 8px 8px 8px' : '8px 0 8px 8px'
                              }}
                            >
                              <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                                {message.message && (
                                  <Typography variant="body2">
                                    {message.message}
                                  </Typography>
                                )}
                                {hasImages && (
                                  <Box sx={{ mt: message.message ? 1 : 0 }}>
                                    {message.image.map((img, idx) => (
                                      <Box
                                        key={idx}
                                        component="img"
                                        src={`${pb.baseUrl}/api/files/mtef6v4qokqh4pv/${message.id}/${img}`}
                                        alt={`Attachment ${idx + 1}`}
                                        sx={{
                                          maxWidth: '100%',
                                          maxHeight: 200,
                                          borderRadius: 1,
                                          mt: 1
                                        }}
                                      />
                                    ))}
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {formatMessageDate(message.created)}
                              {!isFromUser && (
                                <Tooltip title={message.seen ? "Seen" : "Not seen yet"}>
                                  <MarkChatReadIcon 
                                    fontSize="small" 
                                    sx={{ 
                                      ml: 0.5,
                                      fontSize: 14,
                                      color: message.seen ? 'success.main' : 'text.disabled',
                                      verticalAlign: 'middle'
                                    }} 
                                  />
                                </Tooltip>
                              )}
                            </Typography>
                          </Box>
                          
                          {!isFromUser && (
                            <Avatar 
                              sx={{ 
                                width: 32, 
                                height: 32, 
                                ml: 1,
                                mt: 'auto',
                                bgcolor: theme.palette.secondary.main
                              }}
                            >
                              S
                            </Avatar>
                          )}
                        </Box>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Message Input */}
                <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                  <form onSubmit={handleSendMessage}>
                    {selectedImages.length > 0 && (
                      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedImages.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              position: 'relative',
                              width: 60,
                              height: 60,
                              borderRadius: 1,
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              component="img"
                              src={URL.createObjectURL(file)}
                              alt={`Selected ${index}`}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveImage(index)}
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                p: '2px',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    <Grid container spacing={1} alignItems="flex-end">
                      <Grid item>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleFileSelect}
                        />
                        <IconButton
                          color="primary"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={sending}
                        >
                          <AttachFileIcon />
                        </IconButton>
                      </Grid>
                      <Grid item xs>
                        <TextField
                          fullWidth
                          multiline
                          maxRows={4}
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          disabled={sending}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage(e);
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2
                            }
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          endIcon={<SendIcon />}
                          disabled={sending || (!newMessage.trim() && !selectedImages.length)}
                          type="submit"
                          sx={{ borderRadius: 2 }}
                        >
                          Send
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                p: 3,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)'
              }}>
                <MessageIcon sx={{ fontSize: 100, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Select a conversation
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Choose a conversation from the list to view messages
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        <MenuItem 
          onClick={() => handleFilterSelect('all')}
          selected={selectedFilter === 'all'}
        >
          All Messages
        </MenuItem>
        <MenuItem 
          onClick={() => handleFilterSelect('unread')}
          selected={selectedFilter === 'unread'}
        >
          Unread Messages
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SupportMessages;